/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { MutationTree } from "vuex";
import { IInboxState } from "./state";
import { IChat } from "@chatcaptain/types/dist/chatbot";
import { ITag } from "@/types/inbox/tags";
import { IAnswer } from "@/types/inbox/answers";
import { ICustomView } from "@/types/inbox/views";
import { ICustomField } from "@/types/inbox/fields";
import router from "@/router";
import { Unsubscribe } from "firebase/firestore";
import {
  IGroupResult,
  IUser,
  IUserResult,
} from "@chatcaptain/types/dist/organization";
import { store } from "@/store";
import { InboxStore } from "@/store/constants";

export enum Mutations {
  SET_CHATBOT_ID = "SET_CHATBOT_ID",
  SET_CONVERSATIONS = "SET_CONVERSATIONS",
  SET_CONVERSATIONS_LOADING = "SET_CONVERSATIONS_LOADING",
  SET_CURRENT_CONVERSATION = "SET_CURRENT_CONVERSATION",
  SET_CURRENT_CONVERSATION_ID = "SET_CURRENT_CONVERSATION_ID",
  SET_CURRENT_CONVERSATION_LOADING = "SET_CURRENT_CONVERSATION_LOADING",
  CLOSE_CURRENT_CONVERSATION = "CLOSE_CURRENT_CONVERSATION",
  SET_CURRENT_CONVERSATION_UNSUBSCRIBE = "SET_CURRENT_CONVERSATION_UNSUBSCRIBE",
  SET_TAGS = "SET_TAGS",
  SET_ANSWERS = "SET_ANSWERS",
  SET_VIEWS = "SET_VIEWS",
  SET_FIELDS = "SET_FIELDS",
  SET_LOCATION = "SET_LOCATION",
  // SET_USERS = "SET_USERS",
  // SET_GROUPS = "SET_GROUPS",
}

export interface IMutations<S = IInboxState> {
  [Mutations.SET_CHATBOT_ID](state: S, payload: string): void;
  [Mutations.SET_CONVERSATIONS](state: S, payload: IChat[]): void;
  [Mutations.SET_CONVERSATIONS_LOADING](state: S, payload: boolean): void;
  [Mutations.SET_CURRENT_CONVERSATION](state: S, payload: IChat): void;
  [Mutations.SET_CURRENT_CONVERSATION_ID](state: S, payload: string): void;
  [Mutations.SET_CURRENT_CONVERSATION_LOADING](
    state: S,
    payload: boolean
  ): void;
  [Mutations.CLOSE_CURRENT_CONVERSATION](state: S): void;
  [Mutations.SET_CURRENT_CONVERSATION_UNSUBSCRIBE](
    state: S,
    payload: Unsubscribe | null
  ): void;
  [Mutations.SET_TAGS](state: S, payload: ITag[]): void;
  [Mutations.SET_ANSWERS](state: S, payload: IAnswer[]): void;
  [Mutations.SET_VIEWS](state: S, payload: ICustomView[]): void;
  [Mutations.SET_FIELDS](state: S, payload: ICustomField[]): void;
  [Mutations.SET_LOCATION](state: S, payload: IChat["location"] | null): void;
  // [Mutations.SET_USERS](state: S, payload: IUserResult[]): void;
  // [Mutations.SET_GROUPS](state: S, payload: IGroupResult[]): void;
}

export const mutations: MutationTree<IInboxState> & IMutations = {
  [Mutations.SET_CHATBOT_ID]: (state: IInboxState, id: string) => {
    state.chatbotId = id;
  },
  [Mutations.SET_CONVERSATIONS]: (
    state: IInboxState,
    conversations: IChat[]
  ) => {
    state.conversations = conversations;
    store.commit(InboxStore.Mutations.SET_CONVERSATIONS_LOADING, false);
  },
  [Mutations.SET_CONVERSATIONS_LOADING]: (
    state: IInboxState,
    loading: boolean
  ) => {
    state.conversationsLoading = loading;
  },
  [Mutations.SET_CURRENT_CONVERSATION]: (
    state: IInboxState,
    conversation: IChat
  ) => {
    state.currentConversation = conversation;
    state.currentMessages = conversation.messages;
  },
  [Mutations.SET_CURRENT_CONVERSATION_ID]: (state: IInboxState, id: string) => {
    state.currentConversationId = id;
  },
  [Mutations.SET_CURRENT_CONVERSATION_LOADING]: (
    state: IInboxState,
    loading: boolean
  ) => {
    state.currentConversationLoading = loading;
  },
  [Mutations.CLOSE_CURRENT_CONVERSATION]: (state: IInboxState) => {
    if (state.currentConversationUnsubscribe) {
      state.currentConversationUnsubscribe();
      state.currentConversationUnsubscribe = null;
    }
    router.push({
      path:
        "/inbox/" +
        router.currentRoute.value.params.chatbotId +
        "/" +
        (router.currentRoute.value.params.view || "all") +
        "/",
    });
    state.currentConversation = null;
    state.currentConversationId = null;
    state.currentMessages = null;
  },
  [Mutations.SET_CURRENT_CONVERSATION_UNSUBSCRIBE]: (
    state: IInboxState,
    unsubscribe: Unsubscribe | null
  ) => {
    state.currentConversationUnsubscribe = unsubscribe;
  },
  [Mutations.SET_TAGS]: (state: IInboxState, tags: ITag[]) => {
    state.tags = tags;
  },
  [Mutations.SET_ANSWERS]: (state: IInboxState, answers: IAnswer[]) => {
    state.answers = answers;
  },
  [Mutations.SET_VIEWS]: (state: IInboxState, views: ICustomView[]) => {
    state.views = views;
  },
  [Mutations.SET_FIELDS]: (state: IInboxState, fields: ICustomField[]) => {
    state.fields = fields;
  },
  [Mutations.SET_LOCATION]: (
    state: IInboxState,
    location: IChat["location"]
  ) => {
    if (state.currentConversationId) {
      state.location[state.currentConversationId] = location;
    }
  },
  // [Mutations.SET_USERS]: (state: IInboxState, users: IUserResult[]) => {
  //   state.users = users;
  // },
  // [Mutations.SET_GROUPS]: (state: IInboxState, groups: IGroupResult[]) => {
  //   state.groups = groups;
  // },
};
